import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase, Building2, CalendarCheck, CalendarDays, Mail, Phone } from 'lucide-react';
import { InlineSkeleton } from '@/components/core';
import { Avatar, AvatarFallback } from '@/components/ui-kit/avatar';
import { appPath } from '@/constant/app-paths';
import { dateOnlyOf, formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { experienceMonths, formatExperience } from '../../schemas/skills-schema';
import { Employee } from '../../types/employee.types';
import { currentTermOf, currentWorkDaysOf } from './profile-terms';

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const RailRow = ({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) => (
  <div className="grid grid-cols-[16px_minmax(0,1fr)] items-start gap-2.5">
    <span className="mt-0.5 text-muted-foreground [&_svg]:size-4">{icon}</span>
    <div className="grid min-w-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium [overflow-wrap:anywhere]">{children || '-'}</span>
    </div>
  </div>
);

/**
 * The summary beside every tab of Employee details and My Profile (prototype `empRail`): how to reach the
 * person, their department, employment type, joining day with tenure and work days, and their reporting
 * manager. Nothing here is repeated in the profile cards.
 */
export const EmployeeSummaryRail = ({ employee }: { employee: Employee }) => {
  const { t } = useTranslation();
  const term = currentTermOf(employee);
  const workDays = currentWorkDaysOf(term)
    .map((day) => t(day.WorkDay).slice(0, 3))
    .join(', ');
  const tenure = employee.JoiningDate
    ? formatExperience(experienceMonths(dateOnlyOf(employee.JoiningDate), '', true))
    : undefined;

  const managerId = employee.ReportingManagerUserId ?? '';
  const { byUserId, isLoading: isManagerLoading } = useEmployeesByUserIds(
    managerId ? [managerId] : []
  );
  const manager = byUserId.get(managerId);
  const managerName = manager?.DisplayName || employee.ReportingManagerName;

  const managerBody = () => {
    if (managerId && isManagerLoading) return <InlineSkeleton className="w-40" />;
    if (!managerName) return <span className="text-sm text-muted-foreground">{t('NOT_SET')}</span>;
    const body = (
      <>
        <Avatar className="size-7">
          <AvatarFallback className="text-xs">{initialsOf(managerName)}</AvatarFallback>
        </Avatar>
        <span className="grid min-w-0">
          <span className="truncate text-sm font-medium">{managerName}</span>
          {manager?.Designation && (
            <span className="truncate text-xs text-muted-foreground">{manager.Designation}</span>
          )}
        </span>
      </>
    );
    return manager ? (
      <Link
        to={appPath.person(manager.ItemId)}
        className="-m-1.5 flex items-center gap-2.5 rounded-md p-1.5 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {body}
      </Link>
    ) : (
      <div className="flex items-center gap-2.5">{body}</div>
    );
  };

  return (
    <aside
      aria-label={t('SUMMARY')}
      className="grid divide-y rounded-lg border bg-card lg:sticky lg:top-4 lg:self-start"
    >
      <div className="grid gap-3 p-4">
        <RailRow icon={<Mail />} label={t('EMAIL')}>
          {employee.Email && (
            <a
              href={`mailto:${employee.Email}`}
              className="underline decoration-border underline-offset-[3px] hover:decoration-current"
            >
              {employee.Email}
            </a>
          )}
        </RailRow>
        <RailRow icon={<Phone />} label={t('PHONE')}>
          {employee.PhoneNumber}
        </RailRow>
      </div>
      <div className="grid gap-3 p-4">
        <RailRow icon={<Building2 />} label={t('DEPARTMENT')}>
          {employee.DepartmentName}
        </RailRow>
        <RailRow icon={<Briefcase />} label={t('EMPLOYEE_TYPE')}>
          {term?.EmploymentType}
        </RailRow>
        <RailRow icon={<CalendarCheck />} label={t('JOINED')}>
          {employee.JoiningDate && (
            <>
              {formatDateOnly(employee.JoiningDate)}
              {tenure && (
                <span className="block text-xs font-normal text-muted-foreground">
                  {t('TENURE_YEARS_MONTHS', { years: tenure.years, months: tenure.months })}
                </span>
              )}
            </>
          )}
        </RailRow>
        <RailRow icon={<CalendarDays />} label={t('WORK_DAYS')}>
          {workDays}
        </RailRow>
      </div>
      <div className="grid gap-2 p-4">
        <span className="text-xs text-muted-foreground">{t('REPORTING_MANAGER')}</span>
        {managerBody()}
      </div>
    </aside>
  );
};
